import React, { Suspense, useState, useEffect } from 'react';
import { Canvas } from '@react-three/fiber';
import { OrbitControls, Environment, ContactShadows } from '@react-three/drei';
import { motion, AnimatePresence } from 'framer-motion';
import Scene3D from './Scene3D';
import UIOverlay from './UIOverlay';
import LoadingSpinner from './LoadingSpinner';

const DroneShowcase = () => {
  const [isSceneReady, setIsSceneReady] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsSceneReady(true);
      setIsLoading(false);
    }, 2000);
    return () => clearTimeout(timer);
  }, []);

  const handleCanvasError = (error) => {
    console.error('Canvas error:', error);
    setHasError(true);
    setIsLoading(false);
  };

  const handleCanvasCreated = () => {
    setIsLoading(false);
  };

  if (hasError) {
    return (
      <div className="w-full h-screen flex items-center justify-center bg-slate-900">
        <div className="text-center text-white">
          <h2 className="text-2xl font-bold mb-4">Unable to load 3D scene</h2>
          <p className="text-slate-400 mb-4">Please check your browser's WebGL support</p>
          <button
            onClick={() => window.location.reload()}
            className="px-6 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="relative w-full h-screen overflow-hidden bg-slate-900">
      <AnimatePresence>
        {isLoading && (
          <motion.div
            key="loading-spinner"
            initial={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
            className="absolute inset-0 z-50"
          >
            <LoadingSpinner />
          </motion.div>
        )}
      </AnimatePresence>

      {isSceneReady && (
        <motion.div
          className="absolute inset-0"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1 }}
        >
          <Canvas
            camera={{ position: [-12, 6, 18], fov: 60 }}
            gl={{ antialias: true, alpha: true, powerPreference: 'high-performance' }}
            shadows
            className="absolute inset-0"
            onError={handleCanvasError}
            onCreated={handleCanvasCreated}
          >
            <Suspense fallback={null}>
              <color attach="background" args={['#0f172a']} />
              <fog attach="fog" args={['#0f172a', 20, 80]} />
              
              {/* Environment preset for realistic lighting and reflections */}
              <Environment
                preset="sunset"
                background={false}
                environmentIntensity={0.8}
                environmentRotation={[0, Math.PI / 2, 0]}
              />
              
              <Scene3D />
              <OrbitControls
                enablePan={false}
                enableZoom={true}
                maxPolarAngle={Math.PI / 2}
                minDistance={8}
                maxDistance={40}
                autoRotate={true}
                autoRotateSpeed={0.5}
                enableDamping={true}
                dampingFactor={0.05}
              />
              <ContactShadows
                position={[0, -1.9, 0]}
                opacity={0.4}
                scale={20}
                blur={2}
                far={10}
              />
            </Suspense>
          </Canvas>
          <UIOverlay />
        </motion.div>
      )}
    </div>
  );
};

export default DroneShowcase;